import { memoize } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { Svg } from 'react-native-svg';
import * as Rectangle from 'utility/Rectangle';
import PageScrollListener from 'components/PageScrollListener';
import styles from './WindowedSVG.module.css';

// how far above / below current viewport should object element loading begin? in content coordinates
const LOOKAHEAD = 500;

interface Props {
  initialContentOffsetY: number;
  displayWidth: number; // element width of svg pages - effectively the width of this element
  contentHeight: number; // total height of scrolled content in content coordinate space
  contentWidth: number; // width of scrolled content in content coordinate space
  topInset: number;
  onViewportChanged: (minContentY: number, maxContentY: number) => void;
  children: (shouldShow: (cellFrame: Rectangle.Rectangle) => boolean) => JSX.Element;
}

interface State {
  viewportInContentCoordinates: [number, number];
}

class WindowedSVG extends React.Component<Props, State> {
  static defaultProps = {
    contentWidth: 412,
    onViewportChanged: () => {},
  };

  constructor(props: Props) {
    super(props);
    this.state = { viewportInContentCoordinates: [0, 0] };
    this.onScrollCallback = this.onScrollCallback.bind(this);
  }

  queuedScrollEffect: {queuedScrollPosition: [number, number]} | null = null;

  componentDidMount() {
    window.scrollTo(0, this.props.initialContentOffsetY);
  }

  makeShouldShowCallback: (viewportInContentCoordinates: [number, number]) => (cellFrame: Rectangle.Rectangle) => boolean = (
    memoize((viewportInContentCoordinates) => {
      const renderFrame =
        Rectangle.from({
        minX: -Infinity,
        maxX: Infinity,
        minY: viewportInContentCoordinates[0] - LOOKAHEAD,
        maxY: viewportInContentCoordinates[1] + LOOKAHEAD,
      });

      return (cellFrame: Rectangle.Rectangle) => (
        Rectangle.intersects(
          cellFrame,
          renderFrame,
        )
      );
    })
  )

  onScrollCallback(minOffsetY: number, maxOffsetY: number) {
    const {
      contentWidth,
      displayWidth,
      topInset,
      onViewportChanged,
    } = this.props;

    // clientDimension * clientToContent = contentDimension
    const clientToContent = (clientY: number) => clientY * (contentWidth / displayWidth) - topInset;
    const viewportVerticalBounds: [number, number] = [clientToContent(minOffsetY), clientToContent(maxOffsetY)];
    const currentValue = this.queuedScrollEffect;
    if (currentValue != null) {
      currentValue.queuedScrollPosition = viewportVerticalBounds;
      this.queuedScrollEffect = currentValue;
    } else {
      window.setTimeout(() => {
        if (this.queuedScrollEffect == null) {
          return;
        }
        const nextViewportBounds = this.queuedScrollEffect.queuedScrollPosition;
        this.setState({ viewportInContentCoordinates: nextViewportBounds });
        onViewportChanged(...nextViewportBounds);
        this.queuedScrollEffect = null;
      }, 200);
      this.queuedScrollEffect = {
        queuedScrollPosition: viewportVerticalBounds,
      };
    }
  }

  render() {
    const {
      contentWidth,
      contentHeight,
      displayWidth,
      topInset,
      children,
    } = this.props;
    const {
      viewportInContentCoordinates,
    } = this.state;

    return (
      <span className={styles.svg}>
        <View
          style={{
            overflow: 'hidden',
            flexDirection: 'column',
            paddingTop: topInset,
            backgroundColor: 'rgba(249, 249, 249, 1)',
          }}
        >
          <PageScrollListener onScroll={this.onScrollCallback}>
            <Svg
              preserveAspectRatio="xMinYMin"
              viewBox={[0, 0, contentWidth, contentHeight].join(' ')}
              style={{
                overflow: 'visible',
                width: displayWidth,
              }}
            >
              {children(this.makeShouldShowCallback(viewportInContentCoordinates))}
            </Svg>
          </PageScrollListener>
        </View>
      </span>
    );
  }
}

export default WindowedSVG;
